import { useEffect, useRef } from 'react';

const offerItems = [
  'Full AI Voice Agent setup — included',
  'Social AI (WhatsApp, Instagram, Facebook+) — included',
  'Tradify, Jobber, ServiceM8 integrations — included',
  '24/7 answering &amp; lead qualification — included',
  'Onboarding call — included',
  'GDPR-compliant Irish support — included',
  'Zero payment until first job booked — GUARANTEED',
];

export default function TheOffer() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('visible');
        });
      },
      { threshold: 0.1 }
    );
    const reveals = sectionRef.current?.querySelectorAll('.reveal');
    reveals?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative pt-16 pb-24 md:py-16 px-5 overflow-hidden"
      style={{ backgroundColor: '#060d0a' }}
    >
      <div
        className="absolute top-0 left-0 right-0 h-px"
        style={{ background: 'linear-gradient(to right, transparent, rgba(170,255,69,0.3), transparent)' }}
      />

      {/* Ambient glow */}
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-80 h-80 rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(10,92,68,0.2) 0%, transparent 70%)' }}
      />

      <div className="max-w-2xl mx-auto relative z-10">
        {/* Label */}
        <div className="reveal opacity-0 mb-3" style={{ transitionDelay: '0ms' }}>
          <span
            className="font-dm font-semibold text-xs tracking-[0.2em] uppercase"
            style={{ color: '#AAFF45', textShadow: '0 0 10px rgba(170,255,69,0.5)' }}
          >
            ◆ THE NO-BRAINER OFFER
          </span>
        </div>

        {/* Headline */}
        <div className="reveal opacity-0 mb-8" style={{ transitionDelay: '80ms' }}>
          <h2
            className="font-bebas text-brand-text leading-none"
            style={{ fontSize: 'clamp(44px, 13vw, 60px)' }}
          >
            Try Free.<br />
            <span style={{ color: '#AAFF45', textShadow: '0 0 30px rgba(170,255,69,0.3)' }}>
              Until You Book<br />Your First Job.
            </span>
          </h2>
        </div>

        {/* Offer stack */}
        <div className="flex flex-col gap-3 mb-7">
          {offerItems.map((item, i) => {
            const isGuaranteed = item.includes('GUARANTEED');
            return (
              <div
                key={i}
                className="reveal opacity-0 flex items-start gap-3 rounded-xl p-4"
                style={{
                  transitionDelay: `${i * 80}ms`,
                  backgroundColor: isGuaranteed ? 'rgba(170,255,69,0.07)' : 'rgba(10,92,68,0.12)',
                  border: isGuaranteed
                    ? '1px solid rgba(170,255,69,0.3)'
                    : '1px solid rgba(10,92,68,0.3)',
                }}
              >
                <span
                  className="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full mt-0.5"
                  style={{ backgroundColor: '#AAFF45' }}
                >
                  <i className="ri-check-line text-xs" style={{ color: '#080F0C', fontWeight: 700 }} />
                </span>
                <p
                  className="font-dm text-sm leading-snug"
                  style={{ color: isGuaranteed ? '#AAFF45' : '#F5F5F5', fontWeight: isGuaranteed ? 700 : 400 }}
                  dangerouslySetInnerHTML={{ __html: item }}
                />
              </div>
            );
          })}
        </div>

        {/* Risk box */}
        <div
          className="reveal opacity-0 rounded-xl p-5 mb-7"
          style={{
            transitionDelay: '700ms',
            border: '1px solid rgba(10,92,68,0.7)',
            backgroundColor: 'rgba(10,92,68,0.12)',
            boxShadow: '0 0 20px rgba(10,92,68,0.15)',
          }}
        >
          <div className="flex items-start gap-3">
            <span className="text-2xl flex-shrink-0">🔒</span>
            <div>
              <p className="font-dm font-bold text-brand-text text-sm mb-1">OUR PROMISE</p>
              <p className="font-dm text-brand-grey text-sm leading-relaxed">
                If AI doesn't book you a job, you owe us nothing. Not a cent.
              </p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="reveal opacity-0 mb-3" style={{ transitionDelay: '800ms' }}>
          <a
            href="https://apex-emerald-ai.com/contact"
            className="whitespace-nowrap block w-full text-center font-dm font-bold rounded-xl cursor-pointer transition-all duration-200 hover:brightness-110 active:scale-95"
            style={{
              backgroundColor: '#AAFF45',
              color: '#080F0C',
              fontSize: '22px',
              padding: '18px 24px',
              minHeight: '60px',
              boxShadow: '0 0 40px rgba(170,255,69,0.45), 0 4px 24px rgba(0,0,0,0.4)',
            }}
          >
            Claim My Free Trial Now →
          </a>
        </div>

        {/* Below CTA */}
        <p
          className="reveal opacity-0 text-center font-dm text-brand-grey text-xs mb-6"
          style={{ transitionDelay: '860ms' }}
        >
          Under 24hrs setup. Irish support. No contracts.
        </p>

        {/* Urgency */}
        <div
          className="reveal opacity-0 flex items-center justify-center gap-2 py-3 rounded-xl"
          style={{
            transitionDelay: '920ms',
            backgroundColor: 'rgba(170,255,69,0.07)',
            border: '1px solid rgba(170,255,69,0.2)',
          }}
        >
          <span style={{ color: '#AAFF45' }}>⚡</span>
          <p className="font-dm text-sm font-semibold" style={{ color: '#AAFF45' }}>
            10 spots this month —{' '}
            <span className="text-brand-text">6 remaining</span>
          </p>
        </div>
      </div>
    </section>
  );
}
